import React, { useMemo } from "react";
import styled from "styled-components";
import DosagIcon from "@assets/icons/blood-drop.svg";
import FrequencyIcon from "@assets/icons/calendar.svg";
import DurationIcon from "@assets/icons/schedule.svg";
import NeutritionIcon from "@assets/icons/nutrition.svg";
import LinkIcon from "@assets/icons/link.svg";
import Badge from "./Badge";
import mediaQuery from "@utils/mediaQuery";
import { TreatmentObject } from "@hooks/useGetRecommendation";
import urlFor from "@utils/sanityImageBuilder";
import RichText from "./RichText";
import { NoteContainer } from "./Recommendation";
import { Link } from "react-router-dom";
import ROUTES from "@constants/routes";
import { getNzfLink } from "@utils/recommendation";

// Enhanced Complex Dosing Link Component
interface ComplexDosingLinkProps {
  titleDisplay: string;
  isAdult: boolean;
  className?: string;
}

const ComplexDosingLink: React.FC<ComplexDosingLinkProps> = ({ 
  titleDisplay, 
  isAdult, 
  className 
}) => {
  const linkUrl = useMemo(() => {
    const params = new URLSearchParams();
    
    // Add medicine parameter - titleDisplay is already just the medicine name
    if (titleDisplay) {
      params.append('medicine', titleDisplay.toLowerCase());
    }
    
    // Add age parameter
    const ageCategory = isAdult ? 'adult' : 'child';
    params.append('age', ageCategory);
    
    // Add source context for analytics
    params.append('source', 'treatment-recommendation');
    
    return `${ROUTES.dosing.to}?${params.toString()}`;
  }, [titleDisplay, isAdult]);

  return (
    <Link
      to={linkUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      Read protocol
    </Link>
  );
};

// Styled version of the link for consistency
const StyledComplexDosingLink = styled(ComplexDosingLink)`
  color: var(--interactive-primary);
  text-decoration: underline;
  font-weight: var(--font-weight-medium);

  &:hover {
    color: var(--interactive-primary-hover);
    text-decoration: none;
  }
`;

function getFrequencyLabel(frequency: number, formTitle?: string): string {
    const form = formTitle?.toLowerCase().trim() || "";
    const isInjectable = form.includes("injection") || form.includes("infusion");

  if (!frequency || frequency <= 0) return "Single dose";
  if (frequency === 1.52) return "Once weekly";
  if (frequency === 3.7) return "every 3 days";

  switch (frequency) {
    case 1:
      return isInjectable ? "every 24 hours" : "once daily";
    case 2:
      return isInjectable ? "every 12 hours" : "twice daily";
    case 3:
      return isInjectable ? "every 8 hours" : "three times daily";
    case 4:
      return isInjectable ? "every 6 hours" : "four times daily";
    case 6:
      return isInjectable ? "every 4 hours" : "six times daily";
    default:
      return `${frequency}× daily`; //fallback
  }
}

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${mediaQuery("tablet")`
  flex-direction: row;
  justify-content: space-between;
  `}
`;

const TitleContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const IconContainer = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  background-color: var(--bg-card-alt);
  border-radius: 100%;
`;

const Title = styled.a`
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-4xl);
  text-decoration-line: none;
  cursor: pointer;

  ${mediaQuery("tablet")`
  font-size: var(--font-size-4xl);
  line-height: var(--line-height-4xl);
  `}
`;

const Route = styled.div`
  color: var(--text-primary);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-xl);
  margin-bottom: 24px;
`;

const AntimicrobialWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  ${mediaQuery("tablet")`
   gap: 48px;
  `}
`;

const AntimicrobialUnit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  align-items: center;
  color: var(--text-primary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-base);
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border-light);
  margin: 24px 0;
  width: 100%;
`;

interface Props {
  treatment: TreatmentObject;
  isAdult: boolean;
}

const Treatment = ({ treatment, isAdult }: Props) => {
  const {
    duration,
    food,
    frequency,
    antimicrobialUnit,
    titleDisplay,
    dose,
    administration,
    complex,
  } = treatment;

  const { form, doseUnit, approval, antimicrobial } = antimicrobialUnit || {};

  const formTitle = form?.title;
  const unit = doseUnit?.shorthand;
  const nzfLink = getNzfLink(antimicrobial?.nzfLink ?? "", isAdult);
  const iconUrl = form?.icon;

  const showDivider = approval || administration;

// Fixed logic for conditions - more robust checks
const hasFreq = frequency && Number(frequency) >= 1;
const hasDuration = duration !== null && duration !== undefined && Number(duration) > 0;
const hasDose = dose && dose !== null && dose !== undefined;

// Check if we have dose or frequency data for complex dosing logic
const hasBasicData = hasDose || hasFreq;

// New logic: Check if it's a single dose (frequency is 0 or null/undefined)
const isSingleDose = frequency === 0 || !hasFreq;

  return (
    <NoteContainer>
      <TopContent>
        <TitleContainer>
          <IconContainer>
            {iconUrl && (
              <img
                src={urlFor(iconUrl).width(48).height(48).url()}
                alt={`${formTitle} icon`}
              />
            )}
          </IconContainer>
          <TitleWrapper>
            <Title href={nzfLink} target="_blank">
              {titleDisplay}{" "}
              <IconWrapper>
                <LinkIcon />
              </IconWrapper>
            </Title>
            <Route>{formTitle}</Route>
          </TitleWrapper>
        </TitleContainer>
        
        {/* Complex dosing logic */}
        {complex ? (
          <AntimicrobialWrapper>
            {hasBasicData ? (
              /* Show actual dose/frequency data when basic data exists */
              <>
                {hasDose && unit && (
                  <AntimicrobialUnit>
                    <IconWrapper>
                      <DosagIcon />
                    </IconWrapper>
                    <span>
                      {dose}
                      {unit}
                    </span>
                  </AntimicrobialUnit>
                )}

                {hasFreq ? (
                  <AntimicrobialUnit>
                    <IconWrapper>
                      <DurationIcon />
                    </IconWrapper>
                    <span>{getFrequencyLabel(frequency, formTitle)}</span>
                  </AntimicrobialUnit>
                ) : frequency === 0 ? (
                  <AntimicrobialUnit>
                    <IconWrapper>
                      <DurationIcon />
                    </IconWrapper>
                    <span>Single dose</span>
                  </AntimicrobialUnit>
                ) : null}

                {/* Duration only shows if NOT a single dose */}
                {hasDuration && !isSingleDose && (
                  <AntimicrobialUnit>
                    <IconWrapper>
                      <FrequencyIcon />
                    </IconWrapper>
                    <span>for {duration} days</span>
                  </AntimicrobialUnit>
                )}
              </>
            ) : (
              /* Show complex dosing message when no basic data */
              <AntimicrobialUnit>
                <IconWrapper>
                  <DosagIcon />
                </IconWrapper>
                <span>Refer to complex dosing table or calculator</span>
              </AntimicrobialUnit>
            )}
          </AntimicrobialWrapper>
        ) : (
          /* Normal dosing display */
          <AntimicrobialWrapper>
            {/* Show dose if available */}
            {hasDose && unit && (
              <AntimicrobialUnit>
                <IconWrapper>
                  <DosagIcon />
                </IconWrapper>
                <span>
                  {dose}
                  {unit}
                </span>
              </AntimicrobialUnit>
            )}

            {/* Frequency logic */}
            {hasFreq ? (
              /* frequency >= 1: show frequency */
              <AntimicrobialUnit>
                <IconWrapper>
                  <DurationIcon />
                </IconWrapper>
                <span>{getFrequencyLabel(frequency, formTitle)}</span>
              </AntimicrobialUnit>
            ) : frequency === 0 ? (
              /* frequency = 0: display "single dose" */
              <AntimicrobialUnit>
                <IconWrapper>
                  <DurationIcon />
                </IconWrapper>
                <span>Single dose</span>
              </AntimicrobialUnit>
            ) : null}

            {/* Duration logic: show independently if duration >0 AND NOT single dose */}
            {hasDuration && !isSingleDose && (
              <AntimicrobialUnit>
                <IconWrapper>
                  <FrequencyIcon />
                </IconWrapper>
                <span>for {duration} days</span>
              </AntimicrobialUnit>
            )}

            {/* Food instruction */}
            {food && (
              <AntimicrobialUnit>
                <IconWrapper>
                  <NeutritionIcon />
                </IconWrapper>
                <span>with food</span>
              </AntimicrobialUnit>
            )}
          </AntimicrobialWrapper>
        )}
      </TopContent>
      {showDivider && <Divider />}
      {approval && <Badge text="Restricted antimicrobial" />}
      <RichText copy={administration}>
        {complex && (
          <p>
            We recommend reading our complex dosing protocol{" "}
            <StyledComplexDosingLink 
              titleDisplay={titleDisplay}
              isAdult={isAdult}
            />
          </p>
        )}
      </RichText>
    </NoteContainer>
  );
};

export default Treatment;